import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'formik';
import * as Yup from 'yup';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { Box, IconButton, Typography, Chip, Tooltip, Button, MenuItem, DialogTitle, DialogContent } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import RefreshIcon from '@mui/icons-material/Refresh';
import { tableConfig } from '../../../../commons/table/config/tableConfig';
import moment from 'moment';
import AuditarTrasladoDialog from './AuditarTrasladoDialog';
import ConfirmDialog from '../../../../commons/dialog/confirmDialog';
import CustomDialog from '../../../../commons/dialog/customDialog';
import FormikActions from '../../../../commons/formik/formikActions';
import FormikForm from '../../../../commons/formik/formikForm';
import FormikSelect from '../../../../commons/formik/formikSelect';
import FormikTextField from '../../../../commons/formik/formikTextField';
import { StyledDialog } from '../../../../styled/dialog';
import { autorizarTramosBloque, cancelarTramosBloque, getMotivosAnulacionTraslado } from '../../../../../actions/traslados';
import snackbarUtils from '../../../../../utils/snackbarUtils';

const cancelInitialValues = {
  idMotivo: '',
  observaciones: '',
};

const cancelValidation = Yup.object({
  idMotivo: Yup.string().required('El motivo es obligatorio'),
  observaciones: Yup.string().required('Las observaciones son obligatorias'),
});

const tramoTieneAlarmasActivas = (tramo) =>
  Boolean(tramo?.tieneAlarmas || (Array.isArray(tramo?.alarmas) && tramo.alarmas.length > 0));

const PendientesATTable = ({ data = [], onRefresh, isLoading, onVisualizarSiniestro, onVisualizarTraslado, onAuditarTraslado }) => {
  const [auditData, setAuditData] = useState(null);
  const [cancelTramos, setCancelTramos] = useState(null);
  const [cancelFormValues, setCancelFormValues] = useState(null);
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [alarmTramos, setAlarmTramos] = useState(null);
  const tableRef = useRef(null);
  const motivosAnulacion = useSelector((state) => state.traslados.motivosAnulacion);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'idTraslado',
        header: 'Nro Traslado',
        size: 80,
      },
      {
        accessorKey: 'idTramo',
        header: 'Nro Tramo',
        size: 80,
        Cell: ({ cell }) => <b>{cell.getValue()}</b>,
      },
      {
        id: 'nroOrden',
        header: 'Tramo',
        size: 60,
        Cell: ({ row }) => `${row.original.nroOrden} de ${row.original.cantidadTramos}`,
      },
      {
        accessorKey: 'esRetorno',
        header: 'Sentido',
        size: 60,
        Cell: ({ row }) => {
          const isRetorno = row.original.esRetorno;
          return (
            <Chip
              label={isRetorno ? 'Vuelta' : 'Ida'}
              size="small"
              sx={{
                backgroundColor: isRetorno ? '#fff3e0' : '#e3f2fd',
                color: isRetorno ? '#e65100' : '#1565c0',
                fontSize: '0.7rem',
                height: '20px',
              }}
            />
          );
        }
      },
      {
        accessorKey: 'trabajador',
        header: 'Trabajador',
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
            <Typography variant="body2">{row.original.trabajador}</Typography>
            {row.original.posibleNegativo && (
              <Chip
                label="POSIBLE NEGATIVO"
                size="small"
                sx={{
                  backgroundColor: '#ffecb3',
                  color: '#f57f17',
                  fontWeight: 'bold',
                  fontSize: '0.65rem',
                  height: '20px',
                }}
              />
            )}
            {!row.original.posibleNegativo && row.original.estado === 'Rechazado por Agencia' && (
              <Chip
                label="RECHAZADO POR AGENCIA"
                size="small"
                sx={{
                  backgroundColor: '#ffcdd2',
                  color: '#c62828',
                  fontWeight: 'bold',
                  fontSize: '0.65rem',
                  height: '20px',
                }}
              />
            )}
            {Array.isArray(row.original.alarmas) && row.original.alarmas.length > 0 && (
              <Tooltip title={row.original.alarmas.join(' • ')}>
                <Chip
                  label={`ADVERTENCIA: ${row.original.alarmas[0]}`}
                  size="small"
                  sx={{
                    backgroundColor: '#fff3e0',
                    color: '#e65100',
                    fontWeight: 'bold',
                    fontSize: '0.65rem',
                    height: '20px',
                    maxWidth: 240,
                    textOverflow: 'ellipsis',
                  }}
                />
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        accessorKey: 'siniestro',
        header: 'Siniestro',
        size: 50,
      },
      {
        accessorKey: 'origen',
        header: 'Origen',
        muiTableBodyCellProps: {
          sx: { whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '230px' },
        },
      },
      {
        accessorKey: 'destino',
        header: 'Destino',
        muiTableBodyCellProps: {
          sx: { whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '230px' },
        },
      },
      {
        accessorKey: 'fechaTraslado',
        header: 'Fecha',
        size: 50,
        Cell: ({ cell }) =>
          cell.getValue() ? moment(cell.getValue()).format('DD/MM/YYYY') : '-',
      },
      {
        accessorKey: 'horaTraslado',
        header: 'Hora',
        size: 30,
      },
      {
        accessorKey: 'dni',
        header: 'DNI',
        size: 50,
      },
      {
        accessorKey: 'gestor',
        header: 'Gestor',
      },
      {
        accessorKey: 'transporte',
        header: 'Transporte',
      },
      {
        accessorKey: 'agencia',
        header: 'Agencia',
        Cell: ({ cell }) => cell.getValue() || '—',
      },
    ],
    []
  );

  const dispatch = useDispatch();
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (cancelTramos?.length) {
      const idArt = cancelTramos[0]?.idArt;
      dispatch(getMotivosAnulacionTraslado(idArt));
    }
  }, [cancelTramos, dispatch]);

  const authorizeSelected = (tramos) => {
    const idTramos = tramos.map((tramo) => tramo.idTramo);
    const idArt = tramos[0]?.idArt;
    setExporting(true);
    dispatch(autorizarTramosBloque(idTramos, null, false, idArt))
      .then(() => {
        tableRef.current?.resetRowSelection();
        onRefresh();
      })
      .catch(() => {
        snackbarUtils.error("Ocurrió un error al autorizar en bloque.");
      })
      .finally(() => setExporting(false));
  };

  const confirmCancel = () => {
    const idTramos = cancelTramos.map((tramo) => tramo.idTramo);
    setCanceling(true);
    const idArt = cancelTramos[0]?.idArt;
    dispatch(cancelarTramosBloque(idTramos, parseInt(cancelFormValues.idMotivo, 10), cancelFormValues.observaciones, idArt))
      .then(() => {
        tableRef.current?.resetRowSelection();
        setOpenCancelConfirm(false);
        setCancelTramos(null);
        setCancelFormValues(null);
        onRefresh();
      })
      .catch(() => {
        snackbarUtils.error("Ocurrió un error al cancelar en bloque.");
      })
      .finally(() => setCanceling(false));
  };

  const table = useMaterialReactTable({
    ...tableConfig,
    columns,
    data: data || [],
    state: { isLoading },
    enableRowActions: true,
    enableRowSelection: (row) => !row.original.posibleNegativo,
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Tooltip title="Visualizar Siniestro" placement="top">
          <IconButton size="small" onClick={() => onVisualizarSiniestro(row.original)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar traslado (Abre FPT)" placement="top">
          <IconButton size="small" onClick={() => onVisualizarTraslado(row.original)}>
            <LocalShippingIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {onAuditarTraslado && (
          <Tooltip title="Editar / Auditar Traslado Completo (FPT)" placement="top">
            <IconButton color="secondary" size="small" onClick={() => onAuditarTraslado(row.original)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Auditar Tramo / Asignar Agencia" placement="top">
          <IconButton color="primary" size="small" onClick={() => setAuditData(row.original)}>
            <AssignmentTurnedInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    muiTableBodyRowProps: ({ row }) => {
      let backgroundColor = 'inherit';
      let borderLeft = 'none';

      if (row.original?.posibleNegativo) {
        backgroundColor = '#fff9c4';
        borderLeft = '4px solid #ff9800';
      } else if (row.original?.estado === 'Rechazado por Agencia') {
        backgroundColor = '#ffebee';
        borderLeft = '4px solid #f44336';
      }

      return {
        ...tableConfig.muiTableBodyRowProps,
        sx: {
          ...tableConfig.muiTableBodyRowProps?.sx,
          backgroundColor,
          borderLeft,
        }
      };
    },
    renderTopToolbarCustomActions: ({ table }) => {
      const handleCancelarBloque = () => {
        const tramos = table.getSelectedRowModel().flatRows.map(r => r.original);
        if (tramos.some(t => t.posibleNegativo)) {
          snackbarUtils.warning("No se pueden cancelar tramos con Posible Negativo en bloque.");
          return;
        }

        setCancelTramos(tramos);
      };

      const handleExportData = () => {
        setExporting(true);
        setTimeout(() => setExporting(false), 1000);
      };

      return (
        <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Typography variant="h6">Tramos pendientes de validación de datos</Typography>
          <IconButton onClick={onRefresh} title="Refrescar">
            <RefreshIcon />
          </IconButton>
          {table.getIsSomeRowsSelected() || table.getIsAllRowsSelected() ? (
            <Button color="error" variant="contained" onClick={handleCancelarBloque} size="small">
              Cancelar en bloque
            </Button>
          ) : null}
          <Button color="primary" variant="outlined" onClick={handleExportData} size="small" disabled={exporting}>
            Exportar
          </Button>
        </Box>
      );
    },
  });
  tableRef.current = table;
  const alarmCount = alarmTramos?.filter(tramoTieneAlarmasActivas).length || 0;

  return (
    <>
      <MaterialReactTable table={table} />
      {auditData && (
        <AuditarTrasladoDialog
          open={!!auditData}
          onClose={() => setAuditData(null)}
          onAuditarTraslado={onAuditarTraslado}
          data={auditData}
          rol="at"
        />
      )}
      <ConfirmDialog
        open={Boolean(alarmTramos)}
        handleClose={() => setAlarmTramos(null)}
        handleConfirm={() => {
          const tramos = alarmTramos;
          setAlarmTramos(null);
          authorizeSelected(tramos);
        }}
        title="Tramos con advertencias"
        text={`${alarmCount} de los tramos seleccionados tienen advertencias. ¿Desea continuar de todas formas?`}
        warningText="La decisión queda registrada como autorización con alarmas activas."
        confirmText="Continuar"
      />
      <CustomDialog
        open={Boolean(cancelTramos)}
        onClose={() => setCancelTramos(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cancelar tramos en bloque</DialogTitle>
        <FormikForm
          initialValues={cancelInitialValues}
          validationSchema={cancelValidation}
          onSubmit={(values) => {
            setCancelFormValues(values);
            setOpenCancelConfirm(true);
          }}
        >
          <Form>
            <DialogContent>
              <FormikSelect name="idMotivo" label="Motivo *" loading={motivosAnulacion?.isFetching} variant="outlined">
                <MenuItem value="">Seleccionar motivo...</MenuItem>
                {motivosAnulacion?.data?.map((motivo) => (
                  <MenuItem key={motivo.idMotivo} value={motivo.idMotivo}>{motivo.descripcion}</MenuItem>
                ))}
              </FormikSelect>
              <FormikTextField
                name="observaciones"
                label="Observaciones *"
                fullWidth
                multiline
                rows={3}
                margin="dense"
              />
            </DialogContent>
            <StyledDialog.Actions>
              <FormikActions
                handleCancel={() => setCancelTramos(null)}
                submitText="Cancelar tramos"
              />
            </StyledDialog.Actions>
          </Form>
        </FormikForm>
      </CustomDialog>
      <ConfirmDialog
        open={openCancelConfirm}
        handleClose={() => setOpenCancelConfirm(false)}
        handleConfirm={confirmCancel}
        title="¿Está seguro que desea cancelar los tramos seleccionados?"
        text="Se aplicará el mismo motivo y la misma observación a todos los tramos seleccionados."
        warningText="Advertencia: Esta acción no se podrá revertir."
        loading={canceling}
        confirmText="Cancelar tramos"
      />
    </>
  );
};

export default PendientesATTable;
